import { useState } from "react";
import { useAuth } from "../../lib/auth";
import { useIntl } from "../../lib/i18n";
import { api } from "../../lib/api";

// IOLTA reconciliation page. Posts to the admin-only endpoint
// POST /api/v1/tenant/iolta/reconciliation which returns the latest
// per-matter balance roll-up across the firm's trust ledger.
type Summary = {
  matterCount: number;
  totalBalanceCents: number;
};

function formatUsd(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}

export default function IoltaPage() {
  const { session } = useAuth();
  const { t } = useIntl();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [ranAt, setRanAt] = useState<Date | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await api("/api/v1/tenant/iolta/reconciliation", {
        method: "POST",
        token: session?.token,
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const body = await res.text().catch(() => res.statusText);
        throw new Error(body || res.statusText);
      }
      const body = (await res.json()) as Summary;
      setSummary(body);
      setRanAt(new Date());
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold text-brand-900">{t("iolta.title")}</h1>
      <p className="mt-2 text-sm text-slate-500">{t("iolta.subtitle")}</p>

      <div className="mt-6 rounded-md border border-slate-200 bg-white p-4">
        <button
          type="button"
          onClick={run}
          disabled={submitting}
          className="rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-500 disabled:opacity-50"
        >
          {submitting ? t("common.loading") : t("iolta.run")}
        </button>

        {error && <p className="mt-3 text-sm text-rose-600">{error}</p>}

        {summary === null && !error && (
          <p className="mt-4 text-sm text-slate-500">{t("iolta.empty")}</p>
        )}

        {summary && (
          <dl className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-md bg-slate-50 p-3">
              <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                {t("iolta.col.matters")}
              </dt>
              <dd className="mt-1 text-xl font-semibold text-brand-900">
                {summary.matterCount}
              </dd>
            </div>
            <div className="rounded-md bg-slate-50 p-3">
              <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                {t("iolta.col.total")}
              </dt>
              <dd className="mt-1 text-xl font-semibold text-brand-900">
                {formatUsd(summary.totalBalanceCents)}
              </dd>
            </div>
            {ranAt && (
              <div className="rounded-md bg-slate-50 p-3 sm:col-span-2">
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  {t("iolta.col.ranAt")}
                </dt>
                <dd className="mt-1 text-sm text-slate-700">
                  {ranAt.toLocaleString()}
                </dd>
              </div>
            )}
          </dl>
        )}
      </div>
    </div>
  );
}
